import { useRef, useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/kfmApi";
import { toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import {
  ScrollText, NotebookPen, HandHeart, MessagesSquare, Sparkles, Lock, Loader2,
  GripVertical, Plus, Trash2, Copy, Check, ChevronLeft, ArrowRight,
} from "lucide-react";
import { CreditCost, announceCredits } from "@/components/CreditMeter";
import ShareTeachDialog from "@/components/ShareTeachDialog";

// Teach & Share (Pillar 4) — turn a completed study + the reader's OWN reflection into a
// teaching resource. Gated on 3+ Go Deeper exchanges; no spiritual gate, no bare-topic path.
const FORMATS = [
  { id: "sermon_outline", label: "Sermon Outline", icon: ScrollText, multi: true, heavy: true, cost: 4 },
  { id: "teaching_notes", label: "Teaching / Small Group Notes", icon: NotebookPen, multi: true, heavy: true, cost: 3 },
  { id: "devotional", label: "Devotional", icon: HandHeart, multi: false, heavy: false, cost: 1 },
  { id: "discussion_guide", label: "Discussion Guide", icon: MessagesSquare, multi: true, heavy: true, cost: 3 },
  { id: "nugget", label: "Short Teaching Nugget", icon: Sparkles, multi: false, heavy: false, cost: 1 },
];
const AUDIENCE_CHIPS = ["Congregation", "Small group", "Leaders & teams", "Family", "An individual", "Myself"];
const FOOTER = {
  devotional: "Draft only. Shaped by your own reflection.",
  nugget: "Draft only.",
  _default: "Draft only. Prepared with you, not for you.",
};
const fmtMeta = (id) => FORMATS.find((f) => f.id === id) || FORMATS[2];

const FramingLine = () => (
  <p className="text-sm text-slate-400 leading-relaxed">
    Turn what you&rsquo;ve studied into something teachable &mdash; a sermon outline, teaching notes, a
    devotional and more. Built from <span className="text-slate-400">your own study and reflection</span>.
  </p>
);

export default function TeachAndShare({ session }) {
  const passageRef = session.study?.passage_reference || "this passage";
  const exchanges = (session.chat || []).filter((m) => m.role === "user").length;
  const eligible = exchanges >= 3;

  // A finished draft (has blocks) resumes at the output view; an in-progress reflection
  // (autosaved to teach_share_draft) resumes at the reflect step so nothing typed is ever lost.
  const generated = session.teach_share && (session.teach_share.blocks || []).length ? session.teach_share : null;
  const draft = session.teach_share_draft || {};

  const [step, setStep] = useState(generated ? "output" : (draft.format ? "reflect" : "picker"));
  const [fmt, setFmt] = useState(generated?.format || draft.format || "");
  const [nudgeFor, setNudgeFor] = useState("");
  const [meaning, setMeaning] = useState(generated?.meaning || draft.meaning || "");
  const [chip, setChip] = useState("");
  const [audienceOpen, setAudienceOpen] = useState(generated?.audience || draft.audience || "");
  const [personal, setPersonal] = useState(generated?.personal || draft.personal || "");
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState(generated?.title || "");
  const [blocks, setBlocks] = useState(generated?.blocks || []);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const dragIx = useRef(null);

  // Autosave the in-progress reflection (debounced) so navigating away — e.g. tapping a Related
  // passage, which opens a NEW study — never discards what the reader has typed. Skipped once a
  // draft is generated (that persists via generate/save) and while there's nothing worth saving.
  useEffect(() => {
    if (step === "output") return;
    if (!(fmt || meaning.trim() || audienceOpen.trim() || chip || personal.trim())) return;
    const t = setTimeout(() => {
      api.put(`/study/sessions/${session.id}/teach-share/draft`, {
        format: fmt, meaning: meaning.trim(),
        audience: (audienceOpen.trim() || chip), personal: personal.trim(),
      }).catch(() => {});
    }, 700);
    return () => clearTimeout(t);
  }, [fmt, meaning, audienceOpen, chip, personal, step, session.id]);

  const meta = fmtMeta(fmt);
  const audience = (audienceOpen.trim() || chip).trim();
  const canGenerate = meaning.trim().length > 0 && audience.length > 0;

  const pickFormat = (f) => {
    setFmt(f.id);
    if (f.heavy && exchanges < 5) { setNudgeFor(f.id); return; }
    setNudgeFor("");
    setStep("reflect");
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/study/sessions/${session.id}/teach-share`, {
        format: fmt, meaning: meaning.trim(), audience, personal: personal.trim(),
      });
      setTitle(data.title || "Working draft");
      setBlocks(data.blocks || []);
      setStep("output");
      if (typeof data.credits_total === "number") announceCredits(data.credits_total);
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail);
      if (e.response?.status === 402) toast.error(msg || "You're out of study credits — top up or upgrade to keep creating.");
      else toast.error(msg || "Couldn't create that resource right now. Please try again.");
    }
    setLoading(false);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      await api.post(`/study/sessions/${session.id}/teach-share/save`, { title, blocks });
      toast.success("Draft saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save your edits.");
    }
    setSaving(false);
  };

  const editBlock = (i, patch) => setBlocks((b) => b.map((x, ix) => (ix === i ? { ...x, ...patch } : x)));
  const addBlock = () => setBlocks((b) => [...b, { label: "New section", body: "" }]);
  const delBlock = (i) => setBlocks((b) => b.filter((_, ix) => ix !== i));
  const onDrop = (i) => {
    const from = dragIx.current;
    if (from === null || from === i) return;
    setBlocks((b) => {
      const next = [...b];
      const [moved] = next.splice(from, 1);
      next.splice(i, 0, moved);
      return next;
    });
    dragIx.current = null;
  };

  const copyAll = async () => {
    const text = `# ${title}\n\n` + blocks.map((b) => `## ${b.label}\n${b.body}`).join("\n\n");
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Draft copied");
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error("Couldn't copy — please select and copy manually."); }
  };

  // ── LOCKED (fewer than 3 exchanges) ─────────────────────────────────────────
  if (!eligible) {
    return (
      <section data-testid="teach-share-locked" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <ScrollText className="w-4 h-4" strokeWidth={1.5} /> Teach &amp; Share
        </div>
        <FramingLine />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FORMATS.map((f) => (
            <div key={f.id} data-testid={`ts-locked-${f.id}`}
                 className="flex items-center gap-3 rounded-2xl border border-[#1E273D] bg-[#0B0F16]/60 px-4 py-3 opacity-60 cursor-not-allowed">
              <f.icon className="w-5 h-5 text-slate-500" strokeWidth={1.5} />
              <span className="text-sm text-slate-400 font-medium">{f.label}</span>
              <Lock className="w-3.5 h-3.5 text-slate-500 ml-auto" strokeWidth={1.5} />
            </div>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5" data-testid="ts-progress-dots">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`w-2 h-2 rounded-full ${i < exchanges ? "bg-[#00E5FF]" : "bg-[#1E273D]"}`} />
            ))}
          </div>
          <p className="text-xs text-slate-500">Keep exploring &mdash; this opens up once you&rsquo;ve gone a bit deeper.</p>
        </div>
      </section>
    );
  }

  // ── PICKER ──────────────────────────────────────────────────────────────────
  if (step === "picker") {
    return (
      <section data-testid="teach-share-picker" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-5">
        <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-slate-500">
          <ScrollText className="w-4 h-4" strokeWidth={1.5} /> Teach &amp; Share
        </div>
        <FramingLine />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {FORMATS.map((f) => (
            <button key={f.id} data-testid={`ts-format-${f.id}`} onClick={() => pickFormat(f)}
                    className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-all active:scale-[0.98]
                      ${fmt === f.id ? "border-[#00E5FF] bg-[#10222A]" : "border-[#1E273D] hover:border-[#00E5FF]/40 hover:bg-[#10222A]"}`}>
              <f.icon className="w-5 h-5 text-[#00E5FF]" strokeWidth={1.5} />
              <span className="text-sm text-slate-200 font-medium">{f.label}</span>
              <span className="ml-auto"><CreditCost cost={f.cost} /></span>
            </button>
          ))}
        </div>

        {nudgeFor && (
          <div data-testid="ts-depth-nudge" className="rounded-xl bg-[#0E131F] border border-amber-500/30 p-4 space-y-3">
            <p className="text-sm text-slate-400 leading-relaxed">
              This format goes deeper &mdash; you&rsquo;re welcome to continue as-is, or a Devotional or Teaching
              Nugget might fit this session better.
            </p>
            <div className="flex items-center gap-2">
              <button data-testid="ts-nudge-continue" onClick={() => { setNudgeFor(""); setStep("reflect"); }}
                      className="h-9 px-4 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-sm font-medium">
                Continue anyway
              </button>
              <button data-testid="ts-nudge-switch" onClick={() => { setNudgeFor(""); setFmt(""); }}
                      className="h-9 px-4 rounded-full border border-[#29344D] text-slate-400 text-sm hover:border-[#3A4763]">
                Switch format
              </button>
            </div>
          </div>
        )}
      </section>
    );
  }

  // ── REFLECT ─────────────────────────────────────────────────────────────────
  if (step === "reflect") {
    return (
      <section data-testid="teach-share-reflect" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-6">
        <div className="flex items-center justify-between">
          <button data-testid="ts-reflect-back" onClick={() => setStep("picker")}
                  className="text-xs text-slate-500 hover:text-slate-400 flex items-center gap-1">
            <ChevronLeft className="w-4 h-4" /> {meta.label}
          </button>
          <span className="text-[11px] text-slate-500">Before we build this</span>
        </div>
        <h3 className="text-2xl text-white">Before we build this.</h3>

        <div className="space-y-2" data-testid="ts-q1">
          <label className="text-sm font-medium text-slate-300">In your own words &mdash; what is this passage actually saying?</label>
          <Textarea data-testid="ts-q1-input" value={meaning} onChange={(e) => setMeaning(e.target.value)}
                    rows={3} placeholder="Say it however it makes sense to you…"
                    className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30" />
        </div>

        <div className="space-y-2" data-testid="ts-q2">
          <label className="text-sm font-medium text-slate-300">Who is this for?</label>
          <div className="flex flex-wrap gap-2">
            {AUDIENCE_CHIPS.map((c) => (
              <button key={c} data-testid={`ts-audience-${c.replace(/\W+/g, "-").toLowerCase()}`}
                      onClick={() => { setChip(c); setAudienceOpen(""); }}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors
                        ${chip === c && !audienceOpen ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]" : "border-[#29344D] text-slate-400 hover:border-[#00E5FF]/40"}`}>
                {c}
              </button>
            ))}
          </div>
          <Input data-testid="ts-audience-open" value={audienceOpen}
                 onChange={(e) => { setAudienceOpen(e.target.value); if (e.target.value) setChip(""); }}
                 placeholder="…or describe them in your own words"
                 className="h-10 rounded-xl border-[#29344D] focus-visible:ring-[#00E5FF]/30" />
        </div>

        <div className="space-y-2" data-testid="ts-q3">
          <label className="text-sm font-medium text-slate-300">
            Anything from your own life or study you want woven in? <span className="text-slate-500 font-normal">(optional)</span>
          </label>
          <Textarea data-testid="ts-q3-input" value={personal} onChange={(e) => setPersonal(e.target.value)}
                    rows={2} placeholder="A story, a struggle, an observation — or leave this blank."
                    className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30" />
        </div>

        <p className="text-xs text-slate-500 leading-relaxed border-t border-[#1E273D] pt-4">
          This draft is built from your own study and reflection &mdash; a starting point for your preparation,
          not a finished message. Make it yours before you share it.
        </p>

        <div className="flex items-center justify-between">
          <CreditCost cost={meta.cost} />
          <button data-testid="ts-generate" onClick={generate} disabled={!canGenerate || loading}
                  className="h-11 px-5 rounded-full bg-[#00E5FF] text-[#07090E] text-sm font-medium inline-flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#00B8CC] transition-colors">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            Build my {meta.label.split(" / ")[0].toLowerCase()}
          </button>
        </div>
      </section>
    );
  }

  // ── OUTPUT ──────────────────────────────────────────────────────────────────
  return (
    <section data-testid="teach-share-output" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] p-6 space-y-4">
      <div className="text-[11px] uppercase tracking-widest text-[#00E5FF]" data-testid="ts-output-header">
        Your working draft &mdash; shaped by your study of {passageRef}
      </div>
      <Input data-testid="ts-title" value={title} onChange={(e) => setTitle(e.target.value)}
             className="h-12 text-xl border-0 border-b border-[#1E273D] rounded-none px-0 focus-visible:ring-0 focus-visible:border-[#00E5FF]" />

      <div className="space-y-3">
        {blocks.map((b, i) => (
          <div key={i} data-testid={`ts-block-${i}`}
               draggable={meta.multi}
               onDragStart={() => { dragIx.current = i; }}
               onDragOver={(e) => { if (meta.multi) e.preventDefault(); }}
               onDrop={() => onDrop(i)}
               className="rounded-xl border border-[#1E273D] bg-[#10222A] p-3 space-y-2">
            <div className="flex items-center gap-2">
              {meta.multi && (
                <span className="cursor-grab text-slate-500 hover:text-slate-400" title="Drag to reorder" data-testid={`ts-drag-${i}`}>
                  <GripVertical className="w-4 h-4" />
                </span>
              )}
              {meta.multi ? (
                <input data-testid={`ts-block-label-${i}`} value={b.label}
                       onChange={(e) => editBlock(i, { label: e.target.value })}
                       className="flex-1 bg-transparent text-xs font-medium uppercase tracking-widest text-[#00E5FF] outline-none" />
              ) : (
                <span className="flex-1 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">{b.label}</span>
              )}
              {meta.multi && (
                <button data-testid={`ts-block-del-${i}`} onClick={() => delBlock(i)}
                        className="text-slate-500 hover:text-red-500" title="Delete section">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
            <Textarea data-testid={`ts-block-body-${i}`} value={b.body}
                      onChange={(e) => editBlock(i, { body: e.target.value })}
                      rows={Math.min(12, Math.max(2, (b.body || "").split("\n").length + 1))}
                      className="rounded-lg border-[#29344D] bg-[#0E131F] resize-y text-base leading-relaxed text-slate-200 focus-visible:ring-[#00E5FF]/30" />
          </div>
        ))}
      </div>

      {meta.multi && (
        <button data-testid="ts-add-block" onClick={addBlock}
                className="text-sm text-[#00E5FF] hover:underline inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Add a section
        </button>
      )}

      <p className="text-[11px] text-slate-500 border-t border-[#1E273D] pt-3" data-testid="ts-footer">
        {FOOTER[fmt] || FOOTER._default}
      </p>

      <div className="flex items-center justify-between pt-1">
        <button data-testid="ts-new" onClick={() => { setStep("picker"); setFmt(""); }}
                className="text-xs text-slate-500 hover:text-slate-400">Start another</button>
        <div className="flex items-center gap-2">
          <button data-testid="ts-copy" onClick={copyAll}
                  className="h-9 px-3 rounded-full border border-[#29344D] text-slate-400 text-xs hover:border-[#3A4763] inline-flex items-center gap-1.5">
            {copied ? <Check className="w-3.5 h-3.5 text-[#00E5FF]" /> : <Copy className="w-3.5 h-3.5" />} Copy
          </button>
          <button data-testid="ts-save" onClick={saveEdits} disabled={saving}
                  className="h-9 px-4 rounded-full border border-[#29344D] text-slate-400 text-sm font-medium inline-flex items-center gap-1.5 hover:border-[#00E5FF]/50 disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Save
          </button>
          <ShareTeachDialog sessionId={session.id} />
        </div>
      </div>
    </section>
  );
}
